import axios from 'axios';
import { auth } from '../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import signOut from '../functions/userIdentity/signOut';
import { Capacitor } from '@capacitor/core';

let isRefreshing = false;
let failedQueue = [];

// Promise that resolves when Firebase auth is initialized
let authInitialized = false;
let authInitPromise = new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
        authInitialized = true;
        unsubscribe();
        resolve(user);
    });
});

// Helper to wait for auth to be ready
const waitForAuth = async () => {
    if (authInitialized) {
        return auth.currentUser;
    }
    return authInitPromise;
};

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// URLs that should NOT have Firebase token added
const AUTH_ENDPOINTS = [
    '/Firebase/userDetails',
    '/Firebase/setUserDetailsFromToken',
    '/Firebase/firebaseUserDetails',
    '/connect/token',
    '/connect/userinfo',
    '/sw.js',
    '/service-worker.js',
    '/manifest.json',
    '/favicon',
    '.png',
    '.ico',
    '.svg',
    '.jpg',
    '.jpeg',
    '.gif',
    '.woff',
    '.woff2',
    '.ttf',
    '.eot',
    '/static/',
    '/legal/'
];

const shouldSkipToken = (url) => {
    if (!url) return false;
    
    // Check if URL matches known auth endpoints that should NOT have Firebase token
    if (AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint))) {
        return true;
    }
    
    // For HTTP URLs, check if it's our API domain
    if (url.startsWith('http')) {
        // Get API domain from environment variable
        const apiDomain = import.meta.env.VITE_PUBLIC_DOMAIN || '';
        
        // Extract domain without protocol and trailing slash
        const cleanApiDomain = apiDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
        
        // If URL is to our API, DON'T skip (add token)
        if (cleanApiDomain && url.includes(cleanApiDomain)) {
            return false;
        }
        
        // For IDP domain, check if it's in AUTH_ENDPOINTS
        const idpDomain = (import.meta.env.VITE_PUBLIC_IDP_ROOT || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
        if (idpDomain && url.includes(idpDomain)) {
            // AUTH_ENDPOINTS will handle specific paths
            return false;
        }
        
        // All other external domains - skip token
        return true;
    }
    
    return false;
};

/**
 * Get a valid Firebase token with detailed error logging and strict validation
 */
const getValidFirebaseToken = async (user, forceRefresh = false) => {
    if (!user) {
        throw new Error('No user available');
    }

    // Let Firebase SDK handle refresh token validation internally
    // The SDK will throw specific errors if the refresh token is truly invalid
    // Checking user.refreshToken can be unreliable as it may not be populated synchronously
    
    try {
        // Get token result which includes expiration time
        const tokenResult = await user.getIdTokenResult(forceRefresh);

        if (!tokenResult || !tokenResult.token) {
            throw new Error('Empty token result');
        }

        const expirationTime = new Date(tokenResult.expirationTime).getTime();
        const now = Date.now();
        // Use 5 minute buffer for token refresh
        const fiveMinutes = 5 * 60 * 1000;
        const timeUntilExpiry = expirationTime - now;
        const minutesUntilExpiry = Math.round(timeUntilExpiry / 1000 / 60);

        // If token is expired OR expires in less than 5 minutes, force refresh
        // This is NORMAL OAuth behavior - don't treat it as an error
        if ((timeUntilExpiry <= 0 || timeUntilExpiry < fiveMinutes) && !forceRefresh) {
            // Recursively call with forceRefresh = true to get a fresh token
            return getValidFirebaseToken(user, true);
        }

        return tokenResult.token;

    } catch (error) {
        // Handle specific Firebase SDK errors
        if (error.code === 'auth/invalid-refresh-token' || error.code === 'auth/user-disabled') {
            throw new Error('REFRESH_TOKEN_INVALID');
        }

        if (error.code === 'auth/user-token-expired' && forceRefresh) {
            // If we already tried forcing a refresh and still got expired, that's a genuine failure
            throw new Error('TOKEN_EXPIRED');
        }

        if (error.code === 'auth/network-request-failed') {
            // Only retry once for network errors
            if (!forceRefresh) {
                return getValidFirebaseToken(user, true);
            }
        }

        // For other errors, throw them up
        throw error;
    }
};

// Request interceptor to add Firebase ID token to all requests
axios.interceptors.request.use(
    async (config) => {
        // Skip adding token for authentication endpoints
        if (shouldSkipToken(config.url)) {
            return config;
        }

        try {
            // Wait for Firebase auth to be initialized
            await waitForAuth();

            let user = auth.currentUser;

            // If user is null, wait a bit longer for auth state to settle
            // This handles race conditions where auth state hasn't fully restored yet
            if (!user) {
                // Native platforms need more time after browser close
                const maxAttempts = Capacitor.isNativePlatform() ? 10 : 4;
                const delayMs = Capacitor.isNativePlatform() ? 400 : 500;
                
                for (let i = 0; i < maxAttempts; i++) {
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                    user = auth.currentUser;
                    if (user) {
                        break;
                    }
                }
            }

            // If still no user after waiting, let the request proceed anyway
            // The server will return 401 if the token is invalid/missing
            // This prevents premature sign-outs due to temporary auth state issues
            if (!user) {
                // Don't throw error - let server handle it with 401 response
                return config;
            }

            // Get valid token - this will throw if it fails
            const token = await getValidFirebaseToken(user);

            if (!token) {
                // Don't throw error - let server handle it with 401 response
                return config;
            }

            config.headers.Authorization = `Bearer ${token}`;
            return config;

        } catch (error) {
            // For token-related errors, let the request proceed
            // The server will return 401 and the response interceptor will handle it properly
            if (error.message === 'NO_REFRESH_TOKEN' ||
                error.message === 'TOKEN_EXPIRED' ||
                error.message === 'REFRESH_TOKEN_INVALID' ||
                error.message === 'Empty token result') {
                
                return config; // Proceed without token, let server return 401
            }

            // For other errors, throw to prevent request
            throw error;
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh on 401
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Don't retry authentication endpoints
        if (shouldSkipToken(originalRequest?.url)) {
            return Promise.reject(error);
        }

        // If error is 401 and we haven't retried yet
        if (error.response?.status === 401 && !originalRequest._retry) {

            // If already refreshing, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return axios(originalRequest);
                    })
                    .catch(err => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Wait for Firebase auth to be initialized
                await waitForAuth();
                let user = auth.currentUser;

                // If user is null, wait for auth state to settle (same as request interceptor)
                if (!user) {
                    // Wait up to 2 seconds for auth state to become available
                    for (let i = 0; i < 4; i++) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                        user = auth.currentUser;
                        if (user) {
                            break;
                        }
                    }
                }

                if (!user) {
                    processQueue(new Error('User not authenticated'), null);
                    await signOut();
                    window.location.href = '/Welcome';
                    return Promise.reject(error);
                }

                // Don't check user.refreshToken - it may not be populated synchronously
                // Let Firebase SDK handle the refresh and throw proper errors if it fails
                // Try to refresh the token with retries
                let newToken = null;
                let lastError = null;

                for (let attempt = 0; attempt < 3; attempt++) {
                    try {
                        if (attempt > 0) {
                            const delay = Math.pow(2, attempt) * 1000;
                            await new Promise(resolve => setTimeout(resolve, delay));
                        }

                        // Force refresh the token
                        newToken = await getValidFirebaseToken(user, true);

                        if (newToken) {
                            break;
                        }
                    } catch (err) {
                        lastError = err;

                        // Don't retry for these errors
                        if (err.message === 'NO_REFRESH_TOKEN' ||
                            err.message === 'REFRESH_TOKEN_INVALID' ||
                            err.code === 'auth/invalid-refresh-token') {
                            break;
                        }

                        // Retry for network errors
                        if (err.code === 'auth/network-request-failed') {
                            continue;
                        }

                        // For other auth errors, don't retry
                        if (err.code?.startsWith('auth/')) {
                            break;
                        }
                    }
                }

                if (!newToken) {
                    processQueue(lastError || new Error('Token refresh failed'), null);
                    await signOut();
                    window.location.href = '/Welcome';
                    return Promise.reject(lastError || error);
                }

                // Refresh successful - update queue and retry request
                processQueue(null, newToken);
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axios(originalRequest);

            } catch (refreshError) {
                processQueue(refreshError, null);
                await signOut();
                window.location.href = '/Welcome';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default axios;